import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceArea, ReferenceLine
} from 'recharts';
import CDSInfoModal from '../../UI/CDSInfoModal';

/**
 * PHQ-9 Severity Configuration
 * Based on Kroenke et al. (2001) and APA clinical guidelines.
 */
const SEVERITY_CONFIG = [
  { label: 'Minimal', range: '0-4', min: 0, max: 4, color: '#d6e685', bg: 'rgba(214,230,133,0.2)' },
  { label: 'Mild', range: '5-9', min: 5, max: 9, color: '#fdfd96', bg: 'rgba(253,253,150,0.2)' },
  { label: 'Moderate', range: '10-14', min: 10, max: 14, color: '#feb24c', bg: 'rgba(254,178,76,0.2)' },
  { label: 'Mod Severe', range: '15-19', min: 15, max: 19, color: '#fb6a4a', bg: 'rgba(251,106,74,0.2)' },
  { label: 'Severe', range: '20-27', min: 20, max: 27, color: '#cb181d', bg: 'rgba(203,24,29,0.2)' }
];

const getSeverityLabel = (score) => {
  const s = SEVERITY_CONFIG.find(c => score >= c.min && score <= c.max);
  return s ? `${s.label} (${s.range})` : 'Unknown';
};

/** Parse YYYY-MM-DD as local date (not UTC) to avoid timezone shift */
const parseLocalDate = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Simple linear regression for forecasting.
 */
const linearRegression = (x, y) => {
  const n = x.length;
  const sumX = x.reduce((a, b) => a + b, 0);
  const sumY = y.reduce((a, b) => a + b, 0);
  const sumXY = x.reduce((a, xi, i) => a + xi * y[i], 0);
  const sumX2 = x.reduce((a, xi) => a + xi * xi, 0);
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return { slope: 0, intercept: sumY / n };
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  // Model accuracy (R² analog)
  const yPred = x.map(xi => slope * xi + intercept);
  const ssRes = y.reduce((a, yi, i) => a + (yi - yPred[i]) ** 2, 0);
  const mean = sumY / n;
  const ssTot = y.reduce((a, yi) => a + (yi - mean) ** 2, 0);
  const r2 = ssTot === 0 ? 0 : Math.max(0, 1 - ssRes / ssTot);

  return { slope, intercept, r2 };
};

/**
 * Process raw data for a single client into chart-ready format.
 */
export const processClientData = (rawData, clientId) => {
  if (!rawData || rawData.length === 0) return null;

  // Filter for selected client and sort by date
  const clientRows = rawData
    .filter(d => d.clientId === clientId)
    .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));

  if (clientRows.length < 2) return null;

  const scores = clientRows.map(d => d.phq9Score);
  const dates = clientRows.map(d => parseLocalDate(d.date));
  const firstDate = dates[0];

  // Days since first assessment
  const daysSinceFirst = dates.map(d => Math.round((d - firstDate) / 86400000));

  const currentScore = scores[scores.length - 1];
  const firstScore = scores[0];
  const change = currentScore - firstScore;
  const percentChange = firstScore !== 0 ? (change / firstScore * 100) : 0;
  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);
  const durationDays = daysSinceFirst[daysSinceFirst.length - 1];

  // Trend analysis
  let trend;
  if (scores.length >= 3) {
    const recent = scores.slice(-3);
    if (recent[2] < recent[1] && recent[1] < recent[0]) trend = 'decreasing';
    else if (recent[2] > recent[1] && recent[1] > recent[0]) trend = 'increasing';
    else {
      const { slope } = linearRegression([0, 1, 2], recent);
      if (slope < -0.5) trend = 'fluctuating, generally improving';
      else if (slope > 0.5) trend = 'fluctuating, generally worsening';
      else trend = 'stable';
    }
  } else {
    trend = currentScore < firstScore ? 'improving' : currentScore > firstScore ? 'worsening' : 'stable';
  }

  const ratePerMonth = durationDays > 0 ? (change / durationDays * 30) : 0;

  // Historical chart data
  const chartPoints = clientRows.map((d, i) => ({
    date: d.date,
    dateLabel: parseLocalDate(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    phq9Score: d.phq9Score,
    severity: getSeverityLabel(d.phq9Score),
    daysSinceFirst: daysSinceFirst[i]
  }));

  // Forecasting (need >=3 points)
  let forecastPoints = [];
  let expectedScore = null;
  let expectedSeverity = null;
  let modelAccuracy = null;

  if (scores.length >= 3) {
    const { slope, intercept, r2 } = linearRegression(daysSinceFirst, scores);
    modelAccuracy = Math.round(r2 * 100);

    const lastDay = daysSinceFirst[daysSinceFirst.length - 1];
    const lastDate = dates[dates.length - 1];

    for (let w = 1; w <= 8; w++) {
      const futureDay = lastDay + w * 7;
      const predicted = Math.max(0, Math.min(27, slope * futureDay + intercept));
      const futureDate = new Date(lastDate.getTime() + w * 7 * 86400000);
      forecastPoints.push({
        date: futureDate.toISOString().split('T')[0],
        dateLabel: futureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        forecastScore: Math.round(predicted * 10) / 10,
        severity: getSeverityLabel(Math.round(predicted)),
        daysSinceFirst: futureDay
      });
    }

    // 4-week forecast
    const fourWeekDay = lastDay + 28;
    expectedScore = Math.max(0, Math.min(27, slope * fourWeekDay + intercept));
    expectedSeverity = getSeverityLabel(Math.round(expectedScore));
  }

  // Response classification
  const responseCategory =
    currentScore < 5 ? 'Remission' :
    change <= -(firstScore * 0.5) ? 'Response' :
    change <= -(firstScore * 0.25) ? 'Partial Response' :
    'Non-Response';

  return {
    clientId,
    chartPoints,
    forecastPoints,
    currentScore,
    firstScore,
    change,
    percentChange,
    minScore,
    maxScore,
    durationDays,
    assessmentCount: scores.length,
    trend,
    ratePerMonth,
    expectedScore,
    expectedSeverity,
    modelAccuracy,
    responseCategory,
    currentSeverity: getSeverityLabel(currentScore),
    firstDate: clientRows[0].date,
    lastDate: clientRows[clientRows.length - 1].date
  };
};

const CustomTooltip = ({ active, payload, isForecast }) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  const score = isForecast ? d.forecastScore : d.phq9Score;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-bold text-gray-900">{d.dateLabel}</p>
      <p className="text-gray-700">
        {isForecast ? 'Predicted' : 'PHQ-9'} Score: <span className="font-semibold">{typeof score === 'number' ? score.toFixed ? score.toFixed(1) : score : score}</span>
      </p>
      <p className="text-gray-500 text-xs">{d.severity}</p>
      {isForecast && <p className="text-orange-500 text-xs italic mt-1">AI Forecast</p>}
    </div>
  );
};

const IndividualPHQ9Chart = ({ clientData, showForecast = true, chartHeight = 400, pdfMode = false }) => {
  const [showCDSModal, setShowCDSModal] = useState(false);

  if (!clientData) return null;

  const { chartPoints, forecastPoints, clientId } = clientData;

  // Merge historical + forecast for combined chart
  const allPoints = [
    ...chartPoints.map(p => ({ ...p, forecastScore: null })),
    ...forecastPoints.map(p => ({ ...p, phq9Score: null }))
  ];

  // Connect forecast to last historical point
  if (forecastPoints.length > 0 && chartPoints.length > 0) {
    const lastHistorical = chartPoints[chartPoints.length - 1];
    allPoints.splice(chartPoints.length, 0, {
      ...lastHistorical,
      forecastScore: lastHistorical.phq9Score
    });
  }

  return (
    <div className={pdfMode ? undefined : "bg-white rounded-xl shadow-md p-6"} style={pdfMode ? { background: 'white', padding: '12px' } : undefined}>
      {!pdfMode && (
        <>
          <CDSInfoModal isOpen={showCDSModal} onClose={() => setShowCDSModal(false)} chartType="phq9" />

          <div className="flex items-start justify-between mb-1">
            <h2 className="text-xl font-bold text-gray-900">PHQ-9 Tracker: {clientId}</h2>
            <button
              onClick={() => setShowCDSModal(true)}
              title="View CDS logic, methodology, and regulatory disclosure"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 border border-blue-200 hover:border-blue-400 rounded-full px-2 py-0.5 transition-colors flex-shrink-0 ml-3"
            >
              <span className="font-bold">ⓘ</span> View Logic
            </button>
          </div>
          <p className="text-gray-600 text-sm mb-4">Longitudinal Depression Monitoring with Evidence-Based Clinical Decision Support</p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-yellow-800 text-base font-semibold">
              Clinical Decision Support Notice: This tool provides informational analysis based on PHQ-9 standardized scoring guidelines.
              All recommendations must be evaluated by a licensed healthcare professional.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 font-semibold">Current Score</p>
              <p className="text-xl font-bold text-blue-700">{clientData.currentScore}</p>
              <p className="text-xs text-gray-400">{clientData.change >= 0 ? '+' : ''}{clientData.change} from baseline</p>
            </div>
            <div className="rounded-lg p-3 text-center" style={{
              backgroundColor: SEVERITY_CONFIG.find(s => clientData.currentScore >= s.min && clientData.currentScore <= s.max)?.color || '#eee'
            }}>
              <p className="text-xs text-gray-700 font-semibold">Current Severity</p>
              <p className="text-xl font-bold text-gray-900">{clientData.currentSeverity.split(' (')[0]}</p>
            </div>
            <div className="rounded-lg p-3 text-center" style={{
              backgroundColor: clientData.trend.includes('improv') || clientData.trend === 'decreasing' ? '#d4edda' :
                clientData.trend.includes('worsen') || clientData.trend === 'increasing' ? '#f8d7da' : '#d1ecf1'
            }}>
              <p className="text-xs text-gray-700 font-semibold">Trend Pattern</p>
              <p className="text-lg font-bold text-gray-900 capitalize">{clientData.trend}</p>
            </div>
          </div>
        </>
      )}

      {/* Chart */}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <LineChart data={allPoints} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
          {/* Severity background bands */}
          <ReferenceArea y1={0} y2={5} fill="#d6e685" fillOpacity={0.2} />
          <ReferenceArea y1={5} y2={10} fill="#fdfd96" fillOpacity={0.2} />
          <ReferenceArea y1={10} y2={15} fill="#feb24c" fillOpacity={0.2} />
          <ReferenceArea y1={15} y2={20} fill="#fb6a4a" fillOpacity={0.2} />
          <ReferenceArea y1={20} y2={27} fill="#cb181d" fillOpacity={0.2} />

          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="dateLabel"
            tick={{ fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            height={70}
            label={{ value: 'Assessment Date', position: 'insideBottom', offset: -5 }}
          />
          <YAxis
            domain={[0, 27]}
            label={{ value: 'PHQ-9 Score', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip isForecast={false} />} />

          {/* Severity threshold lines */}
          <ReferenceLine y={5} stroke="#999" strokeDasharray="3 3" />
          <ReferenceLine y={10} stroke="#999" strokeDasharray="3 3" />
          <ReferenceLine y={15} stroke="#999" strokeDasharray="3 3" />
          <ReferenceLine y={20} stroke="#999" strokeDasharray="3 3" />

          {/* Historical line */}
          <Line
            type="monotone"
            dataKey="phq9Score"
            stroke="#1f77b4"
            strokeWidth={3}
            dot={{ r: 5, fill: '#1f77b4' }}
            connectNulls={false}
            name="Historical"
          />

          {/* Forecast line */}
          {showForecast && forecastPoints.length > 0 && (
            <Line
              type="monotone"
              dataKey="forecastScore"
              stroke="#ff7f0e"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={{ r: 3, fill: '#ff7f0e' }}
              connectNulls={false}
              name="Forecast"
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Legend */}
      {pdfMode ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginTop: '8px', fontSize: '11px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '20px', height: '2px', backgroundColor: '#1f77b4' }} />
            <span style={{ fontWeight: '600', color: '#374151' }}>Historical Data</span>
          </div>
          {showForecast && forecastPoints.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '20px', height: '0', borderTop: '2px dashed #ff7f0e' }} />
              <span style={{ fontWeight: '600', color: '#374151' }}>AI Forecast (8 weeks)</span>
            </div>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {SEVERITY_CONFIG.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: s.color, border: (s.color === '#fdfd96' || s.color === '#d6e685') ? '1px solid #ccc' : 'none' }} />
                <span style={{ color: '#4b5563' }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-6 mt-3 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-5 h-0.5 bg-blue-600" />
            <span className="font-semibold text-gray-700">Historical Data</span>
          </div>
          {showForecast && forecastPoints.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-5 h-0.5 border-t-2 border-dashed border-orange-500" />
              <span className="font-semibold text-gray-700">AI Forecast (8 weeks)</span>
            </div>
          )}
          <div className="ml-auto flex flex-wrap gap-2">
            {SEVERITY_CONFIG.map((s, i) => (
              <div key={i} className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: s.color, border: s.color === '#fdfd96' || s.color === '#d6e685' ? '1px solid #ccc' : 'none' }} />
                <span className="text-xs text-gray-600">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default IndividualPHQ9Chart;
